import { cn } from '@/lib/utils';
import { useId } from 'react';

/**
 * La bascule segmentée JOINTE de l'éditeur de page (maquettes
 * `EditeurPageEcran`, `Inspecteurs`, `ModeAnimation`; issue #58).
 *
 * The maquette draws two variants and says so in as many words: « les bascules
 * segmentées de la barre sont en segments SÉPARÉS, là où l'éditeur de page
 * utilise la variante JOINTE (bordure commune) ; la revue choisira l'une des
 * deux pour le code » (README, « La barre de thème »). This screen is the
 * editor, so it is the joined one, and it is written once here: seven controls
 * use it - the format, the transition's speed, a temps' speed, the input's
 * format and lines, the sound's playback, the image's width - and seven copies
 * of one border rule would have diverged on the first of them.
 *
 * ## Des RADIOS et pas des boutons
 *
 * A segmented control is one choice among a closed few, which is what a radio
 * group IS: arrows move through it, the state is announced, and
 * `getByRole('radio', { name })` finds a segment. The same arbitration
 * `persona-editor-fields.tsx` states for its slider, and for the same reason -
 * a maquette has no form controls, so it draws what a designer draws.
 *
 * The name of the group comes from a `useId()` and never a literal: two
 * segmented controls of one screen sharing a name would be ONE native group,
 * and the second would clear the first.
 */

export interface SegmentedOption<Value extends string> {
  readonly value: Value;
  readonly label: string;
  /** The native tooltip, when the label alone is shorter than what it means. */
  readonly title?: string;
  /**
   * PÂLIE mais choisissable: the segment is drawn faint and still answers a
   * click. For a choice the screen advises against without forbidding it - « Page
   * suivante » on the feedback page, where picking it earns a refusal at the save
   * (`feedback-page-exit`) rather than a blocked gesture (#176, maquette choix 8).
   */
  readonly dimmed?: boolean;
}

export interface SegmentedProps<Value extends string> {
  /** What the group is FOR, read by assistive technology. Never rendered. */
  readonly label: string;
  readonly value: Value;
  readonly options: readonly SegmentedOption<Value>[];
  readonly readOnly: boolean;
  readonly onChange: (next: Value) => void;
  readonly testId?: string;
  /** Smaller segments, for the inspector's narrow column. */
  readonly size?: 'sm' | 'default';
}

export function Segmented<Value extends string>({
  label,
  value,
  options,
  readOnly,
  onChange,
  testId,
  size = 'default',
}: SegmentedProps<Value>) {
  const name = useId();
  return (
    <div
      role="radiogroup"
      aria-label={label}
      {...(testId === undefined ? {} : { 'data-testid': testId })}
      className="inline-flex overflow-hidden rounded-md border-2 border-gray-300 bg-white"
    >
      {options.map((option, index) => (
        <label
          key={option.value}
          {...(option.title === undefined ? {} : { title: option.title })}
          className={cn(
            'relative cursor-pointer select-none whitespace-nowrap font-semibold transition-colors',
            size === 'sm' ? 'px-2.5 py-1 text-caption' : 'px-3.5 py-1.5 text-small',
            // The JOINED variant: one border for the group, a rule between the
            // segments, and never a gap (see the head).
            index === 0 ? '' : 'border-gray-300 border-l',
            option.value === value ? 'bg-bleu-nuit text-white' : 'text-gray-700 hover:bg-gray-100',
            option.dimmed === true && option.value !== value ? 'text-gray-400' : '',
            readOnly ? 'cursor-not-allowed opacity-60 hover:bg-transparent' : '',
          )}
        >
          {/*
            The radio FILLS the segment rather than being `sr-only`, and that is
            not cosmetic: a 1 px input under a label is an element no pointer can
            reach, so the label intercepts every click and Playwright's `.check()`
            times out on a control an operator uses without noticing. Transparent
            and full-size, the input is the target for both.
          */}
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={option.value === value}
            disabled={readOnly}
            onChange={() => onChange(option.value)}
            className="absolute inset-0 m-0 h-full w-full cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed"
          />
          {option.label}
        </label>
      ))}
    </div>
  );
}
